import type { Manufacturer } from '#/graphql/types';
import { inUseStatuses } from './constants';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { getLocationList, getLocation } from '#/graphql/queries';
import { gql } from '@apollo/client';
import { EmailRule, PatternRule } from 'devextreme-react/validator';

const httpUrlPattern =
  /^(?:(?:https?|ftp|ftps|sftp):\/\/(?:[a-zA-Z0-9._-]+(?::[a-zA-Z0-9._-]+)?@)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*|(?:[a-zA-Z0-9._-]+(?::[a-zA-Z0-9._-]+)?@)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+)(?::\d{1,5})?(?:\/[^\s]*)?$/;
const phonePattern = /^\+?\d{4,17}$/;

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorType: 'TextBox',
    editorOptions: { readOnly: true },
    isRequired: true,
  },
  name: {
    dataField: 'name',
    label: 'Name',
    isRequired: true,
    editorType: 'TextBox',
  },
  description: {
    dataField: 'description',
    label: 'Description',
    editorType: 'TextArea',
    editorOptions: {
      placeholder: 'Enter description',
      autoResizeEnabled: true,
      height: 100,
    },
  },
  countryId: {
    dataField: 'countryId',
    label: 'Country',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search countries...',
      button: {
        tooltip: 'View country details',
        url: '/assets/locations/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getLocationList),
        getItem: gql(getLocation),
        filter: [['level', '=', 'country'], 'and', ['inUseStatus', '=', inUseStatuses.inUse.key]],
      },
    },
  },
  website: {
    dataField: 'website',
    label: 'Website',
    editorType: 'TextBox',
    children: [<PatternRule message="Invalid URL" pattern={httpUrlPattern} />],
  },
  contactPerson: {
    dataField: 'contactPerson',
    label: 'Contact Person',
    isRequired: true,
    editorType: 'TextBox',
  },
  contactEmail: {
    dataField: 'contactEmail',
    label: 'Contact Email',
    editorType: 'TextBox',
    children: [<EmailRule key="email-email" message="Invalid email" />],
  },
  contactPhone: {
    dataField: 'contactPhone',
    label: 'Contact Phone',
    editorType: 'TextBox',
    children: [<PatternRule message="Invalid phone number" pattern={phonePattern} />],
  },
  address: {
    dataField: 'address',
    label: 'Address',
    editorType: 'TextArea',
  },
  warrantyPolicies: {
    dataField: 'warrantyPolicies',
    label: 'Warranty Policies',
    editorType: 'TextBox',
    children: [<PatternRule message="Invalid URL" pattern={httpUrlPattern} />],
  },
  slaDetails: {
    dataField: 'slaDetails',
    label: 'SLA Details',
    editorType: 'TextArea',
  },
  vendorPortalLink: {
    dataField: 'vendorPortalLink',
    label: 'Vendor Portal Link',
    editorType: 'TextBox',
    children: [<PatternRule message="Invalid URL format" pattern={httpUrlPattern} />],
  },
  inUseStatus: {
    dataField: 'inUseStatus',
    label: 'Status',
    editorType: 'Lookup',
    editorOptions: {
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(inUseStatuses),
    },
  },
  thirdPartyId: {
    dataField: 'thirdPartyId',
    label: 'Third Party ID',
    editorType: 'TextBox',
  },
  thirdPartySource: {
    dataField: 'thirdPartySource',
    label: 'Third Party Source',
    editorType: 'TextBox',
  },
} satisfies FieldDefs<Manufacturer>;

export default fields;
